/**
 * DomainList — the Agent Coverage capability map: one row per domain, each
 * expanding to its tool rows.
 *
 * Extracted from AiUsageDashboard.tsx:453-488 (the card, header badges and
 * expansion state) + 1295-1404 (DomainRow, ToolCoverageIcon, Badge). The
 * per-domain status derivation moved into coverageScore.domainStatus so it is
 * unit-tested rather than recomputed inline.
 */
import React, { useState } from 'react';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, CircleDashed, XCircle } from 'lucide-react';
import type { ToolCatalog, ToolCatalogDomain, ToolCatalogRow } from '../../../services/aiControlApi';
import { domainStatus } from './coverageScore';

interface Props {
  domains: ToolCatalogDomain[];
  totals: ToolCatalog['totals'];
  onEditTool: (tool: ToolCatalogRow) => void;
}

const ToolCoverageIcon: React.FC<{ coverage: string }> = ({ coverage }) => {
  if (coverage === 'full') return <CheckCircle2 size={14} className="text-emerald-500" />;
  if (coverage === 'partial') return <AlertCircle size={14} className="text-amber-500" />;
  if (coverage === 'stub') return <CircleDashed size={14} className="text-gray-400" />;
  return <XCircle size={14} className="text-rose-400" />;
};

const Badge: React.FC<{ kind: 'full' | 'partial' | 'stub'; count: number }> = ({ kind, count }) => {
  const cfg = {
    full:    { icon: <CheckCircle2 size={12} />, label: 'Full',    color: 'text-emerald-700 bg-emerald-100' },
    partial: { icon: <AlertCircle size={12} />,  label: 'Partial', color: 'text-amber-700 bg-amber-100' },
    stub:    { icon: <CircleDashed size={12} />, label: 'Stub',    color: 'text-gray-700 bg-gray-100' },
  }[kind];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${cfg.color}`}>
      {cfg.icon}
      <span>{count} {cfg.label}</span>
    </span>
  );
};

const DomainRow: React.FC<{
  domain: ToolCatalogDomain;
  expanded: boolean;
  onToggle: () => void;
  onEditTool: (tool: ToolCatalogRow) => void;
}> = ({ domain, expanded, onToggle, onEditTool }) => {
  const covered = domain.toolCount > 0;
  const status = covered ? domainStatus(domain.tools) : 'none';

  const statusConfig = {
    full:    { icon: <CheckCircle2 size={16} className="text-emerald-500" />, label: 'Fully covered', color: 'text-emerald-700 bg-emerald-100' },
    partial: { icon: <AlertCircle size={16} className="text-amber-500" />,    label: 'Partial',       color: 'text-amber-700 bg-amber-100' },
    stub:    { icon: <CircleDashed size={16} className="text-gray-400" />,    label: 'Stub',          color: 'text-gray-700 bg-gray-100' },
    none:    { icon: <XCircle size={16} className="text-rose-400" />,         label: 'Not covered',   color: 'text-rose-700 bg-rose-100' },
  }[status];

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        disabled={!covered}
        className={`w-full px-4 py-3 min-h-[44px] flex items-center gap-3 text-left hover:bg-stone-50 transition-colors ${!covered ? 'opacity-70 cursor-default' : ''}`}
      >
        {covered ? (
          expanded ? <ChevronDown size={14} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
        ) : (
          <span className="w-[14px]" />
        )}
        {statusConfig.icon}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">{domain.label}</p>
          <p className="text-[11px] text-gray-500">
            {domain.toolCount} {domain.toolCount === 1 ? 'tool' : 'tools'}
            {domain.toolCount > 0 && ` · ${domain.tools.map((t) => t.id).slice(0, 2).join(', ')}${domain.tools.length > 2 ? '…' : ''}`}
          </p>
        </div>
        <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded ${statusConfig.color}`}>{statusConfig.label}</span>
      </button>
      {expanded && covered && (
        <div className="bg-stone-50 px-4 py-2 border-t border-stone-100">
          <table className="w-full">
            <tbody className="divide-y divide-stone-100">
              {domain.tools.map((t) => (
                <tr key={t.id}>
                  <td className="py-2 pr-3 align-top w-6">
                    <ToolCoverageIcon coverage={t.coverage} />
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <p className="text-xs font-mono text-gray-800">{t.id}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{t.description}</p>
                  </td>
                  <td className="py-2 text-right align-top">
                    <p className="text-[10px] text-gray-400">{t.method} {t.path}</p>
                    <p className="text-[10px] text-rose-600 font-mono">{t.requiredFeature}</p>
                    <div className="mt-1 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onEditTool(t)}
                        className="min-h-[44px] px-2 text-[10px] text-blue-600 hover:underline font-medium"
                      >
                        Edit
                      </button>
                      <a
                        href="http://localhost:3000/#/pos/reports"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center min-h-[44px] px-2 text-[10px] text-emerald-600 hover:underline font-medium"
                        title="Open the chat in a new tab and ask a question that would call this tool"
                      >
                        Test
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </li>
  );
};

const DomainList: React.FC<Props> = ({ domains, totals, onEditTool }) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const coveredFraction = totals.domainsTotal > 0
    ? (totals.domainsCovered / totals.domainsTotal) * 100
    : 0;

  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-100">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Agent Coverage</h2>
            <p className="text-[11px] text-gray-500">What the AI agent can currently answer questions about</p>
          </div>
          <div className="flex items-center gap-3 text-[11px]">
            <Badge kind="full" count={totals.full} />
            <Badge kind="partial" count={totals.partial} />
            <Badge kind="stub" count={totals.stub} />
            <span className="text-gray-500">
              {totals.domainsCovered}/{totals.domainsTotal} domains · {totals.total} tools
            </span>
          </div>
        </div>
        <div className="mt-2 h-1.5 w-full bg-stone-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 via-amber-400 to-rose-400"
            style={{ width: `${coveredFraction.toFixed(0)}%` }}
          />
        </div>
      </div>

      <ul className="divide-y divide-stone-100">
        {domains.map((d) => (
          <DomainRow
            key={d.domain}
            domain={d}
            expanded={!!expanded[d.domain]}
            onToggle={() => setExpanded((prev) => ({ ...prev, [d.domain]: !prev[d.domain] }))}
            onEditTool={onEditTool}
          />
        ))}
      </ul>
    </div>
  );
};

export default DomainList;
